import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { WheelGame } from '@/features/public-survey/WheelGame';
import type { WheelItem } from '@/features/public-survey/games/pickPrize';
import { brandkitApi } from '@/features/brands/api';
import { BRAND_DEFAULTS } from '@/lib/theme';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';

/**
 * Task 8.4 — "try a sample game", opt-in.
 *
 * Deliberately never auto-launched. A demo that starts on its own is a
 * salesman; one behind a button someone chose to press is an answer to "what
 * does this actually look like?". That question is worth answering before a
 * manager has any merch to lose.
 *
 * Nothing here touches the API: the prizes are fixtures, the spin is local, no
 * response is recorded and no stock moves. Only the brand's own colours are
 * real, so the manager sees their game rather than a generic one.
 */
const SAMPLE_PRIZES: WheelItem[] = [
  { id: -1, name: 'Sticker pack', rating: 1, available: 500 },
  { id: -2, name: 'Branded cap', rating: 3, available: 50 },
  { id: -3, name: 'VIP pass', rating: 5, available: 3 },
];

export function SampleGameDialog({
  open,
  onOpenChange,
  brandId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  brandId: number;
}) {
  const t = useT();
  const [won, setWon] = useState<WheelItem | null>(null);

  // Read the same source the live game reads — the brand KIT, not the company
  // row. `co_companies.backgroundColor` is the admin chrome colour, and using
  // it painted the wheel white-on-white for any brand with a light background.
  const kitQ = useQuery({
    queryKey: queryKeys.brandKit.byCompany(brandId),
    queryFn: () => brandkitApi.get(brandId),
    enabled: open && Number.isFinite(brandId),
  });

  const primary = kitQ.data?.primaryColor ?? BRAND_DEFAULTS.primaryColor;
  const accent =
    kitQ.data?.accentColor ?? kitQ.data?.secondaryColor ?? BRAND_DEFAULTS.accentColor;

  const close = (v: boolean) => {
    if (!v) setWon(null);
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('sampleGame.title')}</DialogTitle>
          <DialogDescription>{t('sampleGame.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex justify-center">
          {/* `key` remounts the wheel on replay — it keeps its spin state in
              refs, so reusing the instance would leave it stuck on 'done'. */}
          <WheelGame
            key={won ? 'played' : 'fresh'}
            items={SAMPLE_PRIZES}
            primary={primary}
            accent={accent}
            onWin={setWon}
          />
        </div>

        {won && (
          <p className="text-center text-sm text-[var(--color-xk-text)]">
            {t('sampleGame.won', { name: won.name })}
          </p>
        )}

        <div className="flex justify-end gap-2">
          {won && (
            <Button variant="ghost" onClick={() => setWon(null)}>
              {t('sampleGame.again')}
            </Button>
          )}
          <Button onClick={() => close(false)}>{t('common.close')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
